import React, { useEffect, useMemo, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import CircularProgress from '@/Components/CircularProgress/CircularProgress';
import { Dialog, DialogButtons, DialogContent, DialogTitle } from '@/Components/Dialog/Dialog';
import { DobButton, DobCheckbox, DobIcon, DobLabel, DobSelect, DobTextarea } from '@dedalusdiit/dob-components';
import { t } from 'i18next';
import Styles from './JustificationPopup.module.scss';
import JustificationPopupStore, {
    type JustificationProcedureState,
    PLACEHOLDER_JUSTIFICATION_VALUE,
} from './JustificationPopupStore';
import { OrderForWorklist } from '@/Models/GenericWorklist/WorklistResult';

interface JustificationPopupProps {
    isOpen: boolean;
    selectedOrder: OrderForWorklist;
    setNotVisible: () => void;
    refreshWorklist: () => Promise<void>;
}

const JustificationPopup = observer(({ isOpen, selectedOrder, setNotVisible, refreshWorklist }: Readonly<JustificationPopupProps>): React.ReactElement | null => {
    const store = useMemo(() => new JustificationPopupStore(), []);
    const { isLoading, procedures } = store;
    const loadedForRef = useRef<{ orderId: number; patientId: number } | null>(null);

    const order = selectedOrder;

    useEffect(() => {
        if (!isOpen || !order) {
            loadedForRef.current = null;
            return;
        }
        const orderId = order.OrderInfo.OrderID;
        const patientId = order.PatientInfo.ID;
        if (loadedForRef.current?.orderId === orderId && loadedForRef.current?.patientId === patientId) {
            return;
        }
        loadedForRef.current = { orderId, patientId };
        void (async () => {
            try {
                await store.loadProcedures(order);
            }
            catch (err) {
                console.error('Failed to load procedures', err);
            }
        })();
    }, [isOpen, order?.OrderInfo.OrderID, order?.PatientInfo.ID, store]);

    const handleClose = () => {
        setNotVisible();
    };

    const handleSave = async () => {
        if (!order) {
            return;
        }
        await store.save(setNotVisible, refreshWorklist);
    };

    const titleText = `${t('Justification')} ${store.getPatientDisplayName(order)}`;
    const metaText = `${order.PatientInfo.PatientDOB} – ${order.OrderInfo.OrderNumber}`;

    const dialogContent = (): React.ReactElement => {
        if (isLoading) {
            return (
                <div className={Styles.loading}>
                    <CircularProgress />
                </div>
            );
        }
        if (procedures.length === 0) {
            return <div className={Styles.emptyValue}>{t('NoResultsFound')}</div>;
        }
        return (
            <div className={Styles.justificationWrapper}>
                {procedures.map((procedure, index) => (
                    <ProcedureItem
                        key={procedure.OrderProcedure_UID}
                        procedure={procedure}
                        index={index}
                        isLast={index === procedures.length - 1}
                        store={store}
                    />
                ))}
            </div>
        );
    };

    return (
        <Dialog open={isOpen} className={Styles.justificationPopup}>
            <DialogTitle onClose={handleClose}>
                <span id="justification-modal-title">
                    {titleText}
                    <DobIcon
                        name={order.PatientInfo.Gender === 'M' ? 'gender-male' : 'gender-female'}
                        size="16px"
                        style={{ margin: '0 4px', verticalAlign: 'middle' }}
                    />
                    {metaText}
                </span>
            </DialogTitle>

            <DialogContent>
                {dialogContent()}
            </DialogContent>

            <DialogButtons>
                <DobButton onClick={() => void handleSave()} color="primary" disabled={store.isSaving || isLoading}>
                    {t('Save')}
                </DobButton>
                <DobButton onClick={handleClose} color="silent">
                    {t('Cancel')}
                </DobButton>
            </DialogButtons>
        </Dialog>
    );
});

interface ProcedureItemProps {
    procedure: JustificationProcedureState;
    index: number;
    isLast: boolean;
    store: JustificationPopupStore;
}

function ProcedureItem({ procedure, index, isLast, store }: Readonly<ProcedureItemProps>): React.ReactElement {
    const remarkOptions = [
        { value: PLACEHOLDER_JUSTIFICATION_VALUE, label: t('SelectJustification') },
        ...procedure.Remarks
            .map((r) => ({ value: r.ID.toString(), label: r.Name })),
    ];
    const remarkValue = procedure.selectedJustification ?? PLACEHOLDER_JUSTIFICATION_VALUE;

    const statusOptionsList = store.getAvailableStatusOptions(index).map((opt) => ({
        value: opt.value,
        label: opt.label,
    }));
    const selectedStatusOption = statusOptionsList.find(
        (o) => o.value === procedure.changeToStatus,
    ) ?? null;

    const {
        ProcedureModality,
        ProcedureCode,
        ProcedureFullCode,
        ProcedureDescription,
        ProcedureColor,
    } = procedure;

    const procedureText = `${ProcedureCode} (${ProcedureFullCode}) ${ProcedureDescription}`;

    return (
        <div className={Styles.justificationProcedure}>
            <div className={Styles.procedureRow}>
                <span
                    className={Styles.procedureName}
                    style={{ backgroundColor: ProcedureColor }}
                >
                    <span className={Styles.modality}>{ProcedureModality}</span>
                    <span>{procedureText}</span>
                </span>
                <div className={Styles.justificationSelect}>
                    <DobSelect
                        size="medium"
                        optionSelected={remarkOptions.find((o) => o.value === remarkValue)}
                        onChange={(option: { value: string; label: string } | null) => {
                            if (option?.value && option.value !== PLACEHOLDER_JUSTIFICATION_VALUE) {
                                store.setJustificationRemark(index, option.value);
                            }
                        }}
                        placeholder={t('SelectJustification')}
                        options={remarkOptions}
                        menuPosition="absolute"
                    />
                </div>
            </div>

            <DobTextarea
                direction="vertical"
                label={t('Justification')}
                value={procedure.Justification}
                onChange={(e) => store.setJustificationText(index, e.target.value)}
                rows={5}
            />

            <div className={Styles.justificationInfo}>
                {procedure.JustificationUsername && procedure.JustificationDatetimeString && (
                    <label className={Styles.justificationUsername}>
                        {'* '}
                        <span>
                            {procedure.JustificationUsername} {procedure.JustificationDatetimeString}
                        </span>
                    </label>
                )}
                <DobCheckbox
                    className={Styles.justificationRequired}
                    label={t('Required')}
                    checked={procedure.required}
                    onCheckedChange={(checked) => store.setRequired(index, checked === true)}
                />
            </div>

            <div className={Styles.justificationStatus}>
                <DobLabel text={t('Status')} />
                <DobSelect
                    size="medium"
                    optionSelected={selectedStatusOption}
                    onChange={(option: { value: number; label: string } | null) => {
                        if (option?.value != null) {
                            store.setStatus(index, option.value);
                        }
                    }}
                    options={statusOptionsList}
                    menuPosition="absolute"
                />
            </div>

            {!isLast && <hr className={Styles.procedureSeparator} />}
        </div>
    );
}

export default JustificationPopup;
